/**
 * Display formatting for descriptor-typed grid values.
 *
 * A backend column declares what a value *is* (a timestamp, a calendar date);
 * this module decides what the reader sees. It exists apart from the column
 * builders because the same value appears in more than one place — a desktop
 * cell and a mobile card state the same job date, and a date that reads a day
 * early in one of them is a support call.
 *
 * Contains:
 * - formatGridDateTime — datetime / date-only value → display string
 * - MONEY_FORMAT_OPTIONS — fixed two-decimal options for formatNumber money cells
 *
 * Returns plain JS — no JSX.
 */

const DATE_ONLY_PARTS = /^(\d{4})-(\d{2})-(\d{2})/;

/** Fixed two-decimal display for money cells, on top of the user's number_format locale. */
export const MONEY_FORMAT_OPTIONS = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

/**
 * Formats a descriptor datetime value for display.
 *
 * A date-only value carries no timezone, so it is read as calendar parts and
 * built as a local date. Constructing a Date from the bare `YYYY-MM-DD`
 * string instead parses it as UTC midnight, which renders as the previous
 * local evening for every reader west of Greenwich — the date lands a day
 * early.
 *
 * @param {string|number|Date|null} value - The raw descriptor value.
 * @param {object} [options]
 * @param {boolean} [options.dateOnly=false] - Column declares `format: 'date'`.
 * @param {Function} [options.datetimeFormatter] - Host-supplied datetime
 *   formatter (config.formatters.datetime); applies to full timestamps only.
 * @returns {string} The display string; `''` for an empty value, and the
 *   value's own string form when it is not a parseable date.
 */
export function formatGridDateTime(value, { dateOnly = false, datetimeFormatter } = {}) {
  if (!value) return '';
  if (dateOnly) {
    const parts = DATE_ONLY_PARTS.exec(String(value));
    if (parts) {
      return new Date(+parts[1], +parts[2] - 1, +parts[3]).toLocaleDateString();
    }
  }
  if (datetimeFormatter) return datetimeFormatter(value);
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}
